<template>
  <!-- Article (item) row -->
  <div
    v-if="node.type === 'item'"
    ref="rowRef"
    class="kb-layout-node__row kb-layout-node__row--item"
    :class="{
      'kb-layout-node__row--dragging': rowState.type === 'is-dragging',
    }"
  >
    <span ref="handleRef" class="kb-layout-node__handle">
      <q-icon
        :name="toClassicIcon('fa-grip-dots-vertical', 'solid')"
        size="12px"
        class="kb-layout-node__drag-icon"
      />
    </span>
    <q-icon
      name="fa-regular fa-file-lines"
      size="15px"
      class="kb-layout-node__file-icon"
    />
    <span class="kb-layout-node__title">{{ node.title }}</span>
    <c-badge v-if="node.isPublished === false" label="concept" />
    <knowledge-base-layout-delete-control @confirm="onRemove" />

    <c-drop-indicator
      v-if="rowState.type === 'is-dragging-over' && rowState.closestEdge"
      :edge="rowState.closestEdge"
      gap="0.25rem"
    />
  </div>

  <!-- Category / subcategory row -->
  <div
    v-if="node.type !== 'item'"
    ref="rowRef"
    class="kb-layout-node__row kb-layout-node__row--category"
    :class="{
      'kb-layout-node__row--dragging': rowState.type === 'is-dragging',
      'kb-layout-node__row--subcategory': depth > 0,
    }"
  >
    <div class="kb-layout-node__header">
      <span ref="handleRef" class="kb-layout-node__handle">
        <q-icon
          :name="toClassicIcon('fa-grip-dots-vertical', 'solid')"
          size="12px"
          class="kb-layout-node__drag-icon"
        />
      </span>

      <q-icon
        :name="
          node.isOpen ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'
        "
        size="12px"
        class="kb-layout-node__chevron"
        @click="node.isOpen = !node.isOpen"
      />

      <span
        v-if="!node.isRenaming"
        class="kb-layout-node__name"
        @click="node.isRenaming = true"
      >
        {{ node.title }}
      </span>

      <input
        v-if="node.isRenaming"
        ref="renameInput"
        v-model="node.title"
        :placeholder="depth === 0 ? 'Nieuwe categorie' : 'Nieuwe subcategorie'"
        class="kb-layout-node__rename"
        @keyup.enter="node.isRenaming = false"
        @blur="node.isRenaming = false"
      />

      <span class="kb-layout-node__count">{{ countItems(node) }}</span>

      <knowledge-base-layout-delete-control @confirm="onRemove" />
    </div>

    <div
      v-show="node.isOpen"
      ref="childrenRef"
      class="kb-layout-node__children"
      :class="{ 'kb-layout-node__children--over': containerOver }"
    >
      <div class="kb-layout-node__list">
        <knowledge-base-layout-node
          v-for="child in node.children"
          :key="child.uid"
          :node="child"
          :siblings="node.children"
          :depth="depth + 1"
        />
      </div>

      <button
        v-if="depth === 0"
        type="button"
        class="kb-layout-node__add"
        @click="addSubcategory"
      >
        <q-icon name="fa-solid fa-plus" size="12px" /> Subcategorie toevoegen
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { toClassicIcon } from "common/defaultIcons";
import { canPlaceByDescriptor } from "composables/knowledge-base/knowledgeBaseTreeMove";
import type {
  KnowledgeBaseTreeCategoryNode,
  KnowledgeBaseTreeNode,
  KnowledgeBaseTreeNodeType,
} from "models/knowledge-base/knowledge-base-tree.types";
import CDropIndicator from "shared/components/wrappers/list/CDropIndicator.vue";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import KnowledgeBaseLayoutDeleteControl from "./KnowledgeBaseLayoutDeleteControl.vue";

interface Props {
  node: KnowledgeBaseTreeNode;
  siblings: KnowledgeBaseTreeNode[];
  depth: number;
}

const props = defineProps<Props>();

// Drag payload carried on every draggable row; mirrored by the dialog's
// monitor and by the drop-target canDrop checks (depth invariant).
interface KbDragData {
  kbDnd: true;
  uid: string;
  nodeType: KnowledgeBaseTreeNodeType;
  ownsCategory: boolean;
}

type RowState =
  | { type: "idle" }
  | { type: "is-dragging" }
  | { type: "is-dragging-over"; closestEdge: Edge | null };

const rowRef = ref<HTMLElement | null>(null);
const handleRef = ref<HTMLElement | null>(null);
const childrenRef = ref<HTMLElement | null>(null);
const renameInput = ref<HTMLInputElement | null>(null);

const rowState = ref<RowState>({ type: "idle" });
const containerOver = ref(false);

const ownsCategory = computed(
  (): boolean =>
    props.node.type === "category" &&
    props.node.children.some((child) => child.type === "category"),
);

const dragData = (): KbDragData => ({
  kbDnd: true,
  uid: props.node.uid as string,
  nodeType: props.node.type,
  ownsCategory: ownsCategory.value,
});

const readDrag = (data: Record<string | symbol, unknown>): KbDragData | null =>
  data.kbDnd === true ? (data as unknown as KbDragData) : null;

// A node sitting in this row's list would land at level `depth`; inside this
// row's children container it would land at level `depth + 1`.
const canDropAsSibling = (data: Record<string | symbol, unknown>): boolean => {
  const drag = readDrag(data);
  if (drag === null || drag.uid === props.node.uid) {
    return false;
  }
  return canPlaceByDescriptor(drag.nodeType, drag.ownsCategory, props.depth);
};

const canDropIntoContainer = (
  data: Record<string | symbol, unknown>,
): boolean => {
  const drag = readDrag(data);
  if (drag === null || drag.uid === props.node.uid) {
    return false;
  }
  return canPlaceByDescriptor(
    drag.nodeType,
    drag.ownsCategory,
    props.depth + 1,
  );
};

// Dropping onto the sibling that already borders this edge is a no-op; hide
// the indicator in that case.
const isAdjacentNoOp = (
  sourceElement: Element,
  closestEdge: Edge | null,
): boolean => {
  if (!rowRef.value) {
    return false;
  }
  if (closestEdge === "top") {
    return rowRef.value.previousElementSibling === sourceElement;
  }
  if (closestEdge === "bottom") {
    return rowRef.value.nextElementSibling === sourceElement;
  }
  return false;
};

const updateRowOver = (
  selfData: Record<string | symbol, unknown>,
  sourceElement: Element,
): void => {
  const closestEdge = extractClosestEdge(selfData);
  if (isAdjacentNoOp(sourceElement, closestEdge)) {
    if (rowState.value.type !== "idle") {
      rowState.value = { type: "idle" };
    }
    return;
  }
  if (
    rowState.value.type !== "is-dragging-over" ||
    rowState.value.closestEdge !== closestEdge
  ) {
    rowState.value = { type: "is-dragging-over", closestEdge };
  }
};

let cleanup = (): void => {};

onMounted(() => {
  const row = rowRef.value;
  const handle = handleRef.value;
  if (!row || !handle) {
    return;
  }

  const registrations = [
    draggable({
      element: row,
      dragHandle: handle,
      getInitialData: () => ({ ...dragData() }),
      onDragStart: () => {
        rowState.value = { type: "is-dragging" };
      },
      onDrop: () => {
        rowState.value = { type: "idle" };
      },
    }),
    dropTargetForElements({
      element: row,
      canDrop: ({ source }) => canDropAsSibling(source.data),
      getData: ({ input }) =>
        attachClosestEdge(
          { kbDropKind: "row", uid: props.node.uid },
          { element: row, input, allowedEdges: ["top", "bottom"] },
        ),
      getIsSticky: () => true,
      onDragEnter: ({ self, source }) =>
        updateRowOver(self.data, source.element),
      onDrag: ({ self, source }) => updateRowOver(self.data, source.element),
      onDragLeave: () => {
        rowState.value = { type: "idle" };
      },
      onDrop: () => {
        rowState.value = { type: "idle" };
      },
    }),
  ];

  if (childrenRef.value) {
    const container = childrenRef.value;
    registrations.push(
      dropTargetForElements({
        element: container,
        canDrop: ({ source }) => canDropIntoContainer(source.data),
        getData: () => ({
          kbDropKind: "container",
          containerUid: props.node.uid,
        }),
        onDragEnter: () => {
          containerOver.value = true;
        },
        onDrag: () => {
          containerOver.value = true;
        },
        onDragLeave: () => {
          containerOver.value = false;
        },
        onDrop: () => {
          containerOver.value = false;
        },
      }),
    );
  }

  cleanup = combine(...registrations);
});

onUnmounted(() => {
  cleanup();
});

watch(
  renameInput,
  (el) => {
    if (el) {
      void nextTick(() => el.focus());
    }
  },
  { immediate: true },
);

const createUid = (): string => {
  const [random] = crypto.getRandomValues(new Uint32Array(1));
  return `kb-${Date.now()}-${random.toString(36)}`;
};

const countItems = (category: KnowledgeBaseTreeCategoryNode): number =>
  category.children.reduce(
    (total, child) =>
      total + (child.type === "item" ? 1 : child.children.length),
    0,
  );

const onRemove = (): void => {
  const index = props.siblings.indexOf(props.node);
  if (index !== -1) {
    props.siblings.splice(index, 1);
  }
};

const addSubcategory = (): void => {
  if (props.node.type !== "category") {
    return;
  }
  const newNode: KnowledgeBaseTreeCategoryNode = {
    type: "category",
    uuid: null,
    title: "",
    order: props.node.children.length,
    children: [],
    uid: createUid(),
    isOpen: true,
    isRenaming: true,
  };
  props.node.children.push(newNode);
};
</script>

<style scoped lang="scss">
.kb-layout-node {
  &__row {
    @apply tw-relative tw-rounded-lg;

    &--item {
      @apply tw-flex tw-items-center tw-gap-3 tw-bg-gray-50 tw-px-4 tw-py-3;
    }

    &--category {
      @apply tw-flex tw-flex-col tw-gap-4 tw-border tw-border-gray-100 tw-p-4;
    }

    &--subcategory {
      @apply tw-border-none tw-bg-gray-50 tw-pr-0;
    }

    &--dragging {
      @apply tw-opacity-50;
    }
  }

  &__handle {
    @apply tw-inline-flex tw-cursor-move;
  }

  &__drag-icon,
  &__file-icon {
    @apply tw-text-gray-400;
  }

  &__title {
    @apply tw-flex-1 tw-truncate tw-text-sm tw-text-gray-700;
  }

  &__header {
    @apply tw-flex tw-items-center tw-gap-3 tw-pr-4;
  }

  &__chevron {
    @apply tw-cursor-pointer tw-text-gray-500;
  }

  &__name {
    @apply tw-flex-1 tw-cursor-text tw-font-semibold tw-text-gray-900;
  }

  &__rename {
    @apply tw-flex-1 tw-rounded tw-border tw-border-primary tw-px-2 tw-py-1 tw-text-sm;
  }

  &__count {
    @apply tw-text-sm tw-text-gray-400;
  }

  &__children {
    // min-height keeps an empty (sub)category a targetable drop zone so an
    // article can be dropped into it; without it the container collapses to 0px.
    @apply tw-flex tw-min-h-[2rem] tw-flex-col tw-gap-2 tw-rounded-lg tw-transition-colors;

    &--over {
      @apply tw-bg-indigo-50;
    }
  }

  &__list {
    @apply tw-flex tw-flex-col tw-gap-2;
  }

  &__add {
    @apply tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-dashed tw-border-gray-500 tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500 tw-transition hover:tw-border-primary hover:tw-text-primary;
  }
}
</style>
